import { useEffect, useState } from 'react';

import { analytics } from '@/analytics/analytics-service';
import { useAppConfig } from '@/hooks/use-app-config';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useUser } from '@/hooks/use-user';
import { isAgentProxyMode } from '@/utils/agent-proxy';
import { readBranchCookie } from '@/utils/branch-cookie';

/**
 * Use Analytics Identify
 * Sets the analytics user properties for the current user, organization, facility, and agent.
 * De-identifies when the session principal is not a user (e.g. agent-proxy mode).
 * Relies on AnalyticsService's event queue to buffer identify calls made before providers are ready.
 */
export const useAnalyticsIdentify = () => {
  const userInfo = useUser();
  const { data: appConfig } = useAppConfig();
  const { organizationId, facilityId, agentId, orgUrlName, facilitySlug, orgName } =
    useCurrentFacility();
  // The branch cookie only changes across reloads, so read it once.
  const [branchCookie] = useState(readBranchCookie);
  const branch = branchCookie?.name;
  const branchMode = branchCookie?.status;

  useEffect(() => {
    const isAgentProxy = appConfig && isAgentProxyMode(appConfig);

    if (!isAgentProxy && !userInfo?.email) {
      return;
    }

    const properties: any = {};
    if (!isAgentProxy) {
      properties.uuid = userInfo!.userId;
      properties.displayName = userInfo!.name;
      properties.email = userInfo!.email;
    }
    if (branch) {
      properties.branch = branch;
    }
    if (branchMode) {
      properties.branchMode = branchMode;
    }
    if (orgUrlName) {
      properties.orgShortName = orgUrlName;
    }
    if (orgName) {
      properties.orgDisplayName = orgName;
    }
    if (organizationId) {
      properties.organizationId = organizationId;
    }
    if (facilitySlug) {
      properties.agentShortName = facilitySlug;
    }
    if (facilityId) {
      properties.facilityId = facilityId;
    }
    if (agentId) {
      properties.agentId = agentId;
    }
    analytics.identify(isAgentProxy ? '' : userInfo!.email!, properties);
  }, [
    userInfo,
    appConfig,
    organizationId,
    facilityId,
    agentId,
    orgUrlName,
    facilitySlug,
    orgName,
    branch,
    branchMode,
  ]);
};
